import { Icon } from '@dnb/eufemia';
import { chevron_down_medium as ChevronDownIcon, chevron_up_medium as ChevronUpIcon } from '@dnb/eufemia/icons';
import Markdown from '@portals/shared-frontend/components/Markdown';
import type { OpenAPIV3 } from 'openapi-types';
import { useMemo, useState } from 'react';

import type {
  DereferencedOperationObject,
  DereferencedParameterObject,
} from '@/pages/api-documentation/constants/types';
import { mergeParameters } from '@/pages/api-documentation/utils/transformations';

import ApiUrl from './components/ApiUrl';
import Authentication from './components/Authentication';
import Parameters from './components/Parameters';
import RequestBody from './components/RequestBody';
import Responses from './components/Responses';

import { Header, ItemWrapper } from './index.styles';
import { H3S } from '@/pages/api-documentation/styles/Api.Reference.styles';
import { Action, Deprecated } from '@/pages/api-documentation/styles/Api.styles';

type ItemProps = {
  path: string;
  operation: string;
  operationObject: DereferencedOperationObject;
  parameters: (OpenAPIV3.ReferenceObject | OpenAPIV3.ParameterObject)[] | undefined;
  security: ItemSecurity;
  servers: OpenAPIV3.ServerObject[] | undefined;
  compact: boolean;
};

export type ItemSecurity = {
  security: OpenAPIV3.SecurityRequirementObject[] | undefined;
  itemSecurity?: OpenAPIV3.SecurityRequirementObject[];
  schemas:
    | {
        [p: string]: OpenAPIV3.ReferenceObject | OpenAPIV3.SecuritySchemeObject;
      }
    | undefined;
};

const Item = ({ path, operation, operationObject, security, servers, parameters, compact }: ItemProps) => {
  const {
    summary,
    parameters: itemParameters,
    security: itemSecurity,
    description,
    responses,
    tags,
    deprecated,
    requestBody,
    servers: operationServers,
  } = operationObject;

  const [expanded, setExpanded] = useState(false);
  const key = `tags/${tags ? tags.toString().replaceAll(' ', '-') + '/' : ''}operation/${operation}${path}`
    .replaceAll('{', '')
    .replaceAll('}', '');

  const itemParams =
    itemParameters && parameters
      ? mergeParameters(itemParameters as DereferencedParameterObject[], parameters as DereferencedParameterObject[])
      : itemParameters || parameters;

  return useMemo(() => {
    return (
      <ItemWrapper compact={compact} id={key}>
        <div className={`${compact ? 'compact' : ''}`}>
          <Header compact={compact} expanded={expanded} onClick={() => setExpanded(!expanded)}>
            <Action small={false} type={operation}>
              {operation}
            </Action>
            <span>{compact ? path : summary}</span>
            {compact && <Icon icon={expanded ? ChevronUpIcon : ChevronDownIcon} />}
            {deprecated && <Deprecated>Deprecated</Deprecated>}
          </Header>
          <div className={`item-content ${expanded ? 'expanded' : ''}`}>
            {compact && <h3>{summary}</h3>}
            {description && <Markdown>{description}</Markdown>}
            <ApiUrl action={operation} path={path} servers={operationServers ?? servers} />
            {!!security && <Authentication data={{ ...security, itemSecurity }} />}
            {itemParams && (
              <>
                <H3S>Parameters</H3S>
                <Parameters compact={compact} data={itemParams as OpenAPIV3.ParameterObject[]} itemKey={key} />
              </>
            )}

            {!!requestBody && <RequestBody data={requestBody as OpenAPIV3.RequestBodyObject} />}
            {!!responses && <Responses data={responses} />}
          </div>
        </div>
      </ItemWrapper>
    );
  }, [itemParams, responses, requestBody, compact, setExpanded, expanded, compact]);
};

export default Item;
